<template>
  <div>
    <NotificationBar v-if="loadingError">{{loadingError}}</NotificationBar>
    <TabbedBox :loading="loading">
      <template v-slot:tablist>
        <Tab :first="true" tabid="tab-customer-pricing-archived" v-if="isArchived">
          <i class="far fa-archive"></i> Archived Pricelist
        </Tab>
        <Tab :first="true" tabid="tab-customer-pricing-processed" v-if="!isArchived">
          <i class="far fa-cogs"></i> Processed Glass
        </Tab>            
        <Tab tabid="tab-customer-pricing-services" v-if="!isArchived">
          <i class="far fa-tools"></i> Services
        </Tab>
        <Tab tabid="tab-customer-pricing-general" v-if="!isArchived">
          <i class="far fa-box-up"></i> General
        </Tab>
        <Tab tabid="tab-customer-pricing-assembly" v-if="!isArchived">
          <i class="far fa-cogs"></i> Assembly
        </Tab>
        <Tab tabid="tab-customer-pricing-config" v-if="!isArchived">
          <i class="far fa-cogs"></i> Price Configuration
        </Tab>
        <Tab tabid="tab-customer-pricing-activitylog" v-if="!isNewPricelist">
          <i class="far fa-history"></i> Activity Log
        </Tab>
      </template>

      <template v-slot:tabcontent>
        <TabPage :first="true" tabid="tab-customer-pricing-archived" v-if="isArchived">
          Pricelist archived - Pricing details are available through the archived price list              
        </TabPage>

        <!-- Processed Stock Pricing -->
        <TabPage :first="true" tabid="tab-customer-pricing-processed" v-if="!isArchived">
          <div class="row">
            <div class="col-lg-12">
              <h3>Sales Rates</h3>
            </div>
          </div>

          <div class="row my-3" v-if="editing">
            <div class="col-lg-1 col-form-label font-weight-bold">
              Rate
            </div>
            <div class="col-lg-3">
              <FormSelect :options="salesRateOptionList" v-show="editing" v-model="toolbar.applyProcessedRateId" />
            </div>
            <div class="col-lg-2">
              <DropdownMenu title="Apply to">
                <DropdownMenuItem icon="fa-table" @click="applyProcessedRate()">All</DropdownMenuItem>
                <DropdownMenuDivider />
                <template v-for="(priceGroup, index) in processedPriceGroupList">
                  <DropdownMenuItem icon="fa-columns" :key="index" @click="applyProcessedRate(priceGroup.id)">{{priceGroup.name}}</DropdownMenuItem>
                </template>
              </DropdownMenu>
            </div>
          </div>

          <div class="row">
            <div class="col-lg-12">
              <div class="table-responsive table-hover">
                <Spinner v-if="loading" />
                <table class="table collapsible-list" style="width:100%" v-if="!loading">
                  <thead>
                    <tr>
                      <th width="25%">Category</th>
                      <template v-for="(priceGroup, index) in processedPriceGroupList">
                        <th :key="index">{{priceGroup.name}}</th>
                      </template>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(stockCategory, index) in priceListRootData.glass">
                      <tr :key="index" class="collapsible-list-row-header">
                        <td class="font-weight-bold" @click="categoryBarClicked($event, stockCategory, true)">
                          <span v-html="stockCategory.displayName"></span>
                        </td>
                        <template v-for="(priceGroup, priceGroupIndex) in processedPriceGroupList">
                          <td :key="priceGroupIndex" @click="categoryBarClicked($event, stockCategory, false)">

                            <a href="#" class="font-weight-bold" v-show="editing" @click.stop.prevent="displayCategorySalesRatePopover($event, stockCategory, getProcessedCategoryRate(stockCategory, priceGroup.id).id, priceGroup.id)">
                              {{getProcessedCategoryRate(stockCategory, priceGroup.id).name}}
                            </a>
                            <span v-if="!editing" :class="[processedCategoryRateContainsRateChange(stockCategory, priceGroup.id) ? 'pricing-rate-changed font-weight-bold' : '']">
                              {{getProcessedCategoryRate(stockCategory, priceGroup.id).name}}
                            </span>
                            <span v-if="!editing && processedCategoryRateContainsRateVariation(stockCategory, priceGroup.id)" class="pricing-rate-changed">
                              &nbsp;<Tooltip label="Contains Rate Variations" placement="top"><i class="far fa-exclamation-circle"></i></Tooltip>
                            </span>
                            <span v-if="!editing && processedCategoryRateContainsVariation(stockCategory, priceGroup.id)" class="pricing-list-variation">
                              &nbsp;<Tooltip label="Contains Price Variations" placement="top"><i class="far fa-usd-circle"></i></Tooltip>
                            </span>
                            <span v-if="!editing && getCategoryStockListContainsPending(stockCategory, priceGroup.id)" class="pricing-pending-update">
                              &nbsp;<Tooltip label="Contains Pending Sales Prices" placement="top"><i class="far fa-clock"></i></Tooltip>
                            </span>
                            <span v-if="!editing && processedCategoryRateContainsRateChange(stockCategory, priceGroup.id)" class="pricing-rate-changed">
                              &nbsp;<Tooltip :label="`Changed from ${getProcessedCategoryRate(stockCategory, priceGroup.id).prevRateName}`" placement="top"><i class="far fa-exclamation-circle"></i></Tooltip>
                            </span>
                          </td>
                        </template>
                      </tr>
                      <tr :key="`processed-empty-category-${index}`" v-show="stockCategory.expanded && isCategoryEmpty(stockCategory)">
                        <td :colspan="processedPriceGroupList.length + 1" class="text-muted font-italic text-center">
                          This category contains no stock
                        </td>
                      </tr>
                      <template v-for="(stock, stockIndex) in stockCategory.stockList">
                        <tr :key="`${stockIndex}-${index}-stock`" class="" v-show="stockCategory.expanded">
                          <td>
                            <span class="ml-3" /><LinkStock :id="stock.id" :name="stock.name" />
                          </td>
                          <template v-for="(priceGroup, priceGroupIndex) in processedPriceGroupList">
                            <td :key="priceGroupIndex">
                              <PriceListDataCell
                                :stock="stock"
                                :priceGroupId="priceGroup.id"
                                :editing="editing"
                                :salesRateList="salesRateList"
                                @displaySalesRatePopover="displaySalesRatePopover($event, $event.stock, $event.priceGroupId)"
                                @displayPriceVariationPopover="displayPriceVariationPopover($event, $event.stock, $event.priceGroupId)"
                              />
                            </td>
                          </template>
                        </tr>
                      </template>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </TabPage>

        <!-- Services Pricing -->
        <TabPage tabid="tab-customer-pricing-services" v-if="!isArchived">
          <div class="row">
            <div class="col-lg-12">
              <Spinner v-if="loading" />
              <div v-if="!loading">
                <ul class="nav nav-tabs" role="tablist">
                  <Tab v-for="(priceGroup, index) in servicesPriceGroupList" :first="index == 0" :tabid="`tab-services-pricegroup-${index}`" :key="index">
                    {{priceGroup.name}}
                  </Tab>
                </ul>

                <div class="tab-content">
                  <TabPage v-for="(priceGroup, index) in servicesPriceGroupList" :first="index == 0" :tabid="`tab-services-pricegroup-${index}`" :key="index">
                    <div class="row">
                      <div class="col-lg-12">
                        <h3>Sales Rates</h3>
                      </div>
                    </div>

                    <div class="row my-3" v-if="editing">
                      <div class="col-lg-1 col-form-label font-weight-bold">
                        Rate
                      </div>
                      <div class="col-lg-3">
                        <FormSelect :options="salesRateOptionList" v-show="editing" v-model="toolbar.applyServiceRangeRateId" />
                      </div>
                      <div class="col-lg-2">
                        <DropdownMenu title="Apply to">
                          <DropdownMenuItem icon="fa-table" @click="applyServiceRangeRate(priceGroup.id)">All</DropdownMenuItem>
                          <DropdownMenuDivider />
                          <template v-for="(serviceRange, serviceIndex) in priceGroup.servicePriceRanges">
                            <DropdownMenuItem icon="fa-columns" :key="serviceIndex" @click="applyServiceRangeRate(priceGroup.id, serviceRange.id)">{{serviceRange.name}}</DropdownMenuItem>
                          </template>
                        </DropdownMenu>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-lg-12">
                        <div class="table-responsive table-hover">
                          <table class="table collapsible-list" style="width:100%">
                            <thead>
                              <tr>
                                <th width="25%">Category</th>
                                <template v-for="(serviceRange, serviceIndex) in priceGroup.servicePriceRanges">
                                  <th :key="serviceIndex">{{serviceRange.name}}</th>
                                </template>
                              </tr>
                            </thead>
                            <tbody>
                              <template v-for="(stockCategory, categoryIndex) in priceListRootData.glassServices">
                                <tr :key="categoryIndex" class="collapsible-list-row-header">
                                  <td class="font-weight-bold" @click="categoryBarClicked($event, stockCategory, true)">
                                    <span v-html="stockCategory.displayName"></span>
                                  </td>
                                  <template v-for="(serviceRange, serviceIndex) in priceGroup.servicePriceRanges">
                                    <td :key="serviceIndex" @click="categoryBarClicked($event, stockCategory, false)">
                                      <a href="#" class="font-weight-bold" v-show="editing" @click.stop.prevent="displayCategorySalesRatePopover($event, stockCategory, getServiceRangeCategoryRate(stockCategory, priceGroup.id, serviceRange.id).id, priceGroup.id, serviceRange.id)">
                                        {{getServiceRangeCategoryRate(stockCategory, priceGroup.id, serviceRange.id).name}}
                                      </a>
                                      <span v-if="!editing" :class="[serviceRangeCategoryRateContainsRateChange(stockCategory, priceGroup.id, serviceRange.id) ? 'pricing-rate-changed font-weight-bold' : '']">
                                        {{getServiceRangeCategoryRate(stockCategory, priceGroup.id, serviceRange.id).name}}
                                      </span>
                                      <span v-if="!editing && serviceRangeCategoryRateContainsRateVariation(stockCategory, priceGroup.id, serviceRange.id)" class="pricing-rate-changed">
                                        &nbsp;<Tooltip label="Contains Rate Variations" placement="top"><i class="far fa-exclamation-circle"></i></Tooltip>
                                      </span>
                                      <span v-if="!editing && serviceRangeCategoryRateContainsVariation(stockCategory, priceGroup.id, serviceRange.id)" class="pricing-list-variation">
                                        &nbsp;<Tooltip label="Contains Price Variations" placement="top"><i class="far fa-usd-circle"></i></Tooltip>
                                      </span>
                                      <span v-if="!editing && getCategoryStockListContainsPending(stockCategory, priceGroup.id, serviceRange.id)" class="pricing-pending-update">
                                        &nbsp;<Tooltip label="Contains Pending Sales Prices" placement="top"><i class="far fa-clock"></i></Tooltip>
                                      </span>
                                      <span v-if="!editing && serviceRangeCategoryRateContainsRateChange(stockCategory, priceGroup.id, serviceRange.id)" class="pricing-rate-changed">
                                        &nbsp;<Tooltip :label="`Changed from ${getServiceRangeCategoryRate(stockCategory, priceGroup.id, serviceRange.id).prevRateName}`" placement="top"><i class="far fa-exclamation-circle"></i></Tooltip>
                                      </span>
                                    </td>
                                  </template>
                                </tr>
                                <tr :key="`service-empty-category-${categoryIndex}`" v-show="stockCategory.expanded && isCategoryEmpty(stockCategory)">
                                  <td :colspan="priceGroup.servicePriceRanges.length + 1" class="text-muted font-italic text-center">
                                    This category contains no stock
                                  </td>
                                </tr>
                                <template v-for="(stock, stockIndex) in stockCategory.stockList">
                                  <tr :key="`pg${priceGroup.id}-${stockIndex}-${categoryIndex}-stock`" class="" v-show="stockCategory.expanded">
                                    <td>
                                      <span class="ml-3" /><LinkStock :id="stock.id" :name="stock.name" />
                                    </td>
                                    <template v-for="(serviceRange, serviceIndex) in priceGroup.servicePriceRanges">
                                      <td :key="serviceIndex">

                                        <PriceListDataCell
                                          :stock="stock"
                                          :priceGroupId="priceGroup.id"
                                          :serviceRangeId="serviceRange.id"
                                          :editing="editing"
                                          :salesRateList="salesRateList"
                                          @displaySalesRatePopover="displaySalesRatePopover($event, $event.stock, $event.priceGroupId, $event.serviceRangeId)"
                                          @displayPriceVariationPopover="displayPriceVariationPopover($event, $event.stock, $event.priceGroupId, $event.serviceRangeId)"
                                        />

                                      </td>
                                    </template>
                                  </tr>
                                </template>
                              </template>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                  </TabPage>
                </div>
              </div>
            </div>
          </div>
        </TabPage>

        <!-- General Stock Pricing -->
        <TabPage tabid="tab-customer-pricing-general" v-if="!isArchived">
          <div class="row">
            <div class="col-lg-12">
              <h3>Sales Rates</h3>
            </div>
          </div>

          <div class="row my-3" v-if="editing">
            <div class="col-lg-1 col-form-label font-weight-bold">
              Rate
            </div>
            <div class="col-lg-3">
              <FormSelect :options="salesRateOptionList" v-show="editing" v-model="toolbar.applyGeneralRateId" />
            </div>
            <div class="col-lg-2">
              <DropdownMenu title="Apply to">
                <DropdownMenuItem icon="fa-table" @click="applyGeneralRate()">All</DropdownMenuItem>
              </DropdownMenu>
            </div>
          </div>

          <div class="row">
            <div class="col-lg-12">
              <Spinner v-if="loading" />
              <div class="table-responsive table-hover" v-if="!loading">
                <table class="table collapsible-list" style="width:100%">
                  <thead>
                    <tr>
                      <th width="25%">Category</th>
                      <th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(stockCategory, index) in priceListRootData.general">
                      <tr :key="index" class="collapsible-list-row-header">
                        <td class="font-weight-bold" @click="categoryBarClicked($event, stockCategory, true)">
                          <span v-html="stockCategory.displayName"></span>
                        </td>
                        <td @click="categoryBarClicked($event, stockCategory, false)">
                          <a href="#" class="font-weight-bold" v-show="editing" @click.stop.prevent="displayCategorySalesRatePopover($event, stockCategory, getGeneralCategoryRate(stockCategory).id)">
                            {{getGeneralCategoryRate(stockCategory).name}}
                          </a>
                          <span v-if="!editing" :class="[generalCategoryRateContainsRateChange(stockCategory) ? 'pricing-rate-changed font-weight-bold' : '']">
                            {{getGeneralCategoryRate(stockCategory).name}}
                          </span>
                          <span v-if="!editing && generalCategoryRateContainsRateVariation(stockCategory)" class="pricing-rate-changed">
                            &nbsp;<Tooltip label="Contains Rate Variations" placement="top"><i class="far fa-exclamation-circle"></i></Tooltip>
                          </span>
                          <span v-if="!editing && generalCategoryRateContainsVariation(stockCategory)" class="pricing-list-variation">
                            &nbsp;<Tooltip label="Contains Price Variations" placement="top"><i class="far fa-usd-circle"></i></Tooltip>
                          </span>
                          <span v-if="!editing && getCategoryStockListContainsPending(stockCategory)" class="pricing-pending-update">
                            &nbsp;<Tooltip label="Contains Pending Sales Prices" placement="top"><i class="far fa-clock"></i></Tooltip>
                          </span>
                          <span v-if="!editing && generalCategoryRateContainsRateChange(stockCategory)" class="pricing-rate-changed">
                            &nbsp;<Tooltip :label="`Changed from ${getGeneralCategoryRate(stockCategory).prevRateName}`" placement="top"><i class="far fa-exclamation-circle"></i></Tooltip>
                          </span>
                        </td>
                      </tr>
                      <tr :key="`general-empty-category-${index}`" v-show="stockCategory.expanded && isCategoryEmpty(stockCategory)">
                        <td :colspan="2" class="text-muted font-italic text-center">
                          This category contains no stock
                        </td>
                      </tr>
                      <template v-for="(stock, stockIndex) in stockCategory.stockList">
                        <tr :key="`${stockIndex}-${index}-stock`" class="" v-show="stockCategory.expanded">
                          <td>
                            <span class="ml-3" /><LinkStock :id="stock.id" :name="stock.name" />
                          </td>
                          <td>
                            <PriceListDataCell
                              :stock="stock"
                              :editing="editing"
                              :salesRateList="salesRateList"
                              @displaySalesRatePopover="displaySalesRatePopover($event, $event.stock)"
                              @displayPriceVariationPopover="displayPriceVariationPopover($event, $event.stock)"
                            />
                          </td>
                        </tr>
                      </template>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </TabPage>

        <!-- Assembly Stock Pricing -->
        <TabPage tabid="tab-customer-pricing-assembly" v-if="!isArchived">
          <div class="row">
            <div class="col-lg-12">
              <h3>Sales Rates</h3>
            </div>
          </div>

          <div class="row my-3" v-if="editing">
            <div class="col-lg-1 col-form-label font-weight-bold">
              Rate
            </div>
            <div class="col-lg-3">
              <FormSelect :options="salesRateOptionList" v-show="editing" v-model="toolbar.applyAssemblyRateId" />
            </div>
            <div class="col-lg-2">
              <DropdownMenu title="Apply to">
                <DropdownMenuItem icon="fa-table" @click="applyAssemblyRate()">All</DropdownMenuItem>
              </DropdownMenu>
            </div>
          </div>

          <div class="row">
            <div class="col-lg-12">
              <Spinner v-if="loading" />
              <div class="table-responsive table-hover" v-if="!loading">
                <table class="table collapsible-list" style="width:100%">
                  <thead>
                    <tr>
                      <th width="25%">Category</th>
                      <th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(stockCategory, index) in priceListRootData.assembly">
                      <tr :key="index" class="collapsible-list-row-header">
                        <td class="font-weight-bold" @click="categoryBarClicked($event, stockCategory, true)">
                          <span v-html="stockCategory.displayName"></span>
                        </td>
                        <td @click="categoryBarClicked($event, stockCategory, false)">
                          <a href="#" class="font-weight-bold" v-show="editing" @click.stop.prevent="displayCategorySalesRatePopover($event, stockCategory, getAssemblyCategoryRate(stockCategory).id)">
                            {{getAssemblyCategoryRate(stockCategory).name}}
                          </a>
                          <span v-if="!editing" :class="[assemblyCategoryRateContainsRateChange(stockCategory) ? 'pricing-rate-changed font-weight-bold' : '']">
                            {{getAssemblyCategoryRate(stockCategory).name}}
                          </span>
                          <span v-if="!editing && assemblyCategoryRateContainsRateVariation(stockCategory)" class="pricing-rate-changed">
                            &nbsp;<Tooltip label="Contains Rate Variations" placement="top"><i class="far fa-exclamation-circle"></i></Tooltip>
                          </span>
                          <span v-if="!editing && assemblyCategoryRateContainsVariation(stockCategory)" class="pricing-list-variation">
                            &nbsp;<Tooltip label="Contains Price Variations" placement="top"><i class="far fa-usd-circle"></i></Tooltip>
                          </span>
                          <span v-if="!editing && getCategoryStockListContainsPending(stockCategory)" class="pricing-pending-update">
                            &nbsp;<Tooltip label="Contains Pending Sales Prices" placement="top"><i class="far fa-clock"></i></Tooltip>
                          </span>
                          <span v-if="!editing && assemblyCategoryRateContainsRateChange(stockCategory)" class="pricing-rate-changed">
                            &nbsp;<Tooltip :label="`Changed from ${getAssemblyCategoryRate(stockCategory).prevRateName}`" placement="top"><i class="far fa-exclamation-circle"></i></Tooltip>
                          </span>
                        </td>
                      </tr>
                      <tr :key="`assembly-empty-category-${index}`" v-show="stockCategory.expanded && isCategoryEmpty(stockCategory)">
                        <td :colspan="2" class="text-muted font-italic text-center">
                          This category contains no stock
                        </td>
                      </tr>
                      <template v-for="(stock, stockIndex) in stockCategory.stockList">
                        <tr :key="`${stockIndex}-${index}-stock`" class="" v-show="stockCategory.expanded">
                          <td>
                            <span class="ml-3" /><LinkStock :id="stock.id" :name="stock.name" />
                          </td>
                          <td>
                            <PriceListDataCell
                              :stock="stock"
                              :editing="editing"
                              :salesRateList="salesRateList"
                              @displaySalesRatePopover="displaySalesRatePopover($event, $event.stock)"
                              @displayPriceVariationPopover="displayPriceVariationPopover($event, $event.stock)"
                            />
                          </td>
                        </tr>
                      </template>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </TabPage>

        <TabPage tabid="tab-customer-pricing-config" v-if="!isArchived">
              
          <CustomerStockPriceGroupList v-model="customerPriceList.priceData.priceGroupOverrideSettings" @update="$emit('priceGroupOverrideSettingsUpdated', $event)" :editing="editing">
            <template v-slot:title>
              <ContentTitle>Customer Price Configuration</ContentTitle>
              <ContentSubTitle>Overrides for the global &amp; stock price settings</ContentSubTitle>
            </template>
          </CustomerStockPriceGroupList>
        </TabPage>

        <TabPage tabid="tab-customer-pricing-activitylog" v-if="!isNewPricelist">
          <div class="row">
            <div class="col-lg-12">
              <ActivityLogStream v-if="customerPriceList.priceList" ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.purchaseOrder" :linkId="customerPriceList.priceList.id">
                <template v-slot:title><h3>Recent Activity</h3></template>
              </ActivityLogStream>
            </div>
          </div>
        </TabPage>

      </template>
    </TabbedBox>

    <StockPriceListRatePopover
      ref="salesRateCategoryPopover"
      :pricingSalesRateList="salesRateList"
      :showPricing="false"
      @applyCategoryRate="applySalesRateToCategory($event.stockCategoryId, $event.salesRateId, $event.stockPriceGroupId, $event.serviceRangeId)">
    </StockPriceListRatePopover>

    <StockPriceListRatePopover
      ref="salesRatePopover"
      :pricingSalesRateList="salesRateList"
      @applyStockRate="applySalesRateVariationByStockId($event.stockId, $event.salesRateId, $event.stockPriceGroupId, $event.serviceRangeId)"
      @removeStockRateVariation="removeSalesRateVariationByStockId($event.stockId, $event.stockPriceGroupId, $event.serviceRangeId)">
    </StockPriceListRatePopover>

    <StockPriceListPricePopover
      ref="priceVariationPopover"
      :pricingSalesRateList="salesRateList"
      @applyPriceVariation="updateStockPriceWithVariationByStockId($event.stockId, $event.price, $event.stockPriceGroupId, $event.serviceRangeId)"
      @removePriceVariation="removeStockPriceVariationByStockId($event.stockId, $event.stockPriceGroupId, $event.serviceRangeId)">
    </StockPriceListPricePopover>
  </div>
</template>

<script>
import _ from 'lodash'
import { round } from 'mathjs'
import * as Types from '@/store/types'
import pdfMixin from '@/mixins/pdf'

import Utils from '@/utils/utils'

import DataDisplayMixin from '@/mixins/dataDisplay'
import CustomerPriceListMixin from '@/modules/pricing/mixins/customerPriceListMixin.js'

import PriceListDataCell from '@/modules/pricing/components/priceListDataCell'
import StockPriceListRatePopover from '@/modules/pricing/components/stockPriceListRatePopover'
import StockPriceListPricePopover from '@/modules/pricing/components/stockPriceListPricePopover'

import CustomerStockPriceGroupList from '@/modules/pricing/components/customerStockPriceGroupList'

import { LinkTypes, InventoryStockTypes, StockPriceTypes, CustomerOverrideTypes, CustomerOverrideLevelTypes, ApprovalTypes, PriceListDataSourceTypes, PriceListIntegrationTypes } from '@/store/types'

export default {
  name: "CustomerPriceList",
  mixins: [pdfMixin, DataDisplayMixin, CustomerPriceListMixin],
  components: {
    PriceListDataCell,
    StockPriceListRatePopover,
    StockPriceListPricePopover,
    CustomerStockPriceGroupList
  },
  props: {
    priceListId: Number,
    contactId: Number,
    editing: {
      type: Boolean,
      default: false
    },
    priceListIntegrationType: String
  },
  data() {
    return {
      customerData: {},      
      salesRateOptionList: [],
      
      toolbar: {
        applyProcessedRateId: 0,
        applyServiceRangeRateId: 0,
        applyGeneralRateId: 0,
        applyAssemblyRateId: 0
      },

      loading: false,
    }
  },
  computed: {
    activityStreamURL() {
      if(!this.contactId || !this.priceListId)
        return ''

      return `/pricing/customer/${this.contactId}/pricelists/${this.priceListId}/activitystream`
    },

    isNewPricelist() {
      return !this.priceListId
    },
    isArchived() {
      if(!this.$data.customerPriceList.priceList)
        return false
      return this.$data.customerPriceList.priceList.isArchived
    },
    isActive() {
      if(!this.$data.customerPriceList.priceList)
        return false
      return this.$data.customerPriceList.priceList.isActive
    },
    isScheduled() {
      if(!this.$data.customerPriceList.priceList)
        return false
      return this.$data.customerPriceList.priceList.isScheduled
    },
    isApproved() {
      if(!this.$data.customerPriceList.priceList)
        return false
      return this.$data.customerPriceList.priceList.isApproved
    }
  },
  watch: {
    contactId: function() {
      this.reloadData()
    },
    priceListId: function() {
      this.reloadData()
    },
    priceListIntegrationType: function() {
      this.reloadData()
    }
  },
  created() {
    this.LinkTypes = LinkTypes
  },
  mounted() {
    this.requestData()
  },
  methods: {
    reloadData() {
      if(this.$refs.activityStream)
        this.$refs.activityStream.reloadData()
      this.requestData()
    },
    requestData() {
      this.loading = true

      if(this.contactId) {
        this.$apiService.contactService.getContact(this.contactId).then(response => {
          this.$data.customerData = response.data

          this.$emit('customerLoaded', this.customerData)
        })
      }

      // Calculate how to source pricing data for the editor
      var priceRequestDataSource = PriceListDataSourceTypes.approvedPricing
      if(this.priceListIntegrationType == PriceListIntegrationTypes.includePending || this.priceListIntegrationType == PriceListIntegrationTypes.seperatePriceRequestLinkSource)
        priceRequestDataSource = PriceListDataSourceTypes.pendingPricing

      this.requestCustomerPriceListData(this.contactId, this.priceListId, priceRequestDataSource)
      .then(() => {
        this.$data.salesRateOptionList = this.$data.salesRateList.map(sales => {
          return {
            name: sales.name,
            value: sales.id
          }
        })

        // Set rate dropdowns to the default rate
        if(this.$data.defaultSalesRate) {
          this.$data.toolbar.applyProcessedRateId = this.$data.defaultSalesRate.id
          this.$data.toolbar.applyServiceRangeRateId = this.$data.defaultSalesRate.id
          this.$data.toolbar.applyGeneralRateId = this.$data.defaultSalesRate.id
          this.$data.toolbar.applyAssemblyRateId = this.$data.defaultSalesRate.id
        }

        // If a new price list, set all dropdowns to be default sales rate
        if(this.priceListId == null) {
          this.applyProcessedRate()
          this.applyServiceRangeRate()
          this.applyGeneralRate()
          this.applyAssemblyRate()
        }

        this.$emit('priceGroupOverrideSettingsUpdated', this.customerPriceList.priceData.priceGroupOverrideSettings)
        this.$emit('priceListLoaded', this.customerPriceList)
      })
      .finally(() => {
        this.loading = false
      })
    },

    // Tools
    // Apply a sales rate to all or one price group column in the processed glass page
    applyProcessedRate(priceGroupId) {
      if(this.toolbar.applyProcessedRateId == null)
        return

      this.glassStockCategoryList.forEach(stockCategory => {
        this.processedPriceGroupList.forEach(priceGroup => {
          if(priceGroupId == undefined || priceGroup.id == priceGroupId) {

            this.setProcessedCategoryRate(stockCategory, priceGroup.id, this.toolbar.applyProcessedRateId)
          }
        })
      })
    },
    // Apply a sales rate to all or one service price range column for a price group on the service page
    applyServiceRangeRate(priceGroupId, servicePriceRangeId) {
      if(this.toolbar.applyServiceRangeRateId == null)
        return

      this.serviceStockCategoryList.forEach(stockCategory => {
        this.servicesPriceGroupList.forEach(priceGroup => {
          if(priceGroupId == undefined || priceGroup.id == priceGroupId) {

            priceGroup.servicePriceRanges.forEach(serviceRange => {
              if(servicePriceRangeId == undefined || serviceRange.id == servicePriceRangeId) {

                this.setServiceRangeCategoryRate(stockCategory, priceGroup.id, serviceRange.id, this.toolbar.applyServiceRangeRateId)
              }
            })
          }
        })
      })
    },
    // Apply a sales rate to all the general categories
    applyGeneralRate() {
      if(this.toolbar.applyGeneralRateId == null)
        return

      this.generalStockCategoryList.forEach(stockCategory => {
        this.setGeneralCategoryRate(stockCategory, this.toolbar.applyGeneralRateId)
      })
    },
    // Apply a sales rate to all the assembly categories
    applyAssemblyRate() {
      if(this.toolbar.applyAssemblyRateId == null)
        return

      this.assemblyStockCategoryList.forEach(stockCategory => {
        this.setAssemblyCategoryRate(stockCategory, this.toolbar.applyAssemblyRateId)
      })
    },

    displayCategorySalesRatePopover(evt, category, selectedSalesRateId, stockPriceGroupId = null, serviceRangeId = null) {

      this.$refs.salesRateCategoryPopover.displayCategorySelectionFromElement(evt.target, category.id, stockPriceGroupId, serviceRangeId, selectedSalesRateId)
    },

    displaySalesRatePopover(evt, stock, stockPriceGroupId = null, serviceRangeId = null) {
      var priceData = stock.priceData
      if(stockPriceGroupId)
        priceData = stock.priceGroups[stockPriceGroupId]
      if(stockPriceGroupId && serviceRangeId)
        priceData = stock.priceGroups[stockPriceGroupId][serviceRangeId]

      var selectedSalesRateId = 0
      if(priceData.isRateVariation)
        selectedSalesRateId = priceData.salesRateId

      this.$refs.salesRatePopover.displayPriceSelectionFromElement(evt.target, stock.id, stockPriceGroupId, serviceRangeId, selectedSalesRateId, priceData.salesPrice, priceData.isRateVariation)
    },
    displayPriceVariationPopover(evt, stock, stockPriceGroupId = null, serviceRangeId = null) {
      var priceData = stock.priceData
      if(stockPriceGroupId)
        priceData = stock.priceGroups[stockPriceGroupId]
      if(stockPriceGroupId && serviceRangeId)
        priceData = stock.priceGroups[stockPriceGroupId][serviceRangeId]

      this.$refs.priceVariationPopover.displayPriceSelectionFromElement(evt.target, stock.id, stockPriceGroupId, serviceRangeId, priceData.price, priceData.salesPrice, priceData.isVariation)
    },
  }
}
</script>